"use client"

import { router, usePage } from "@inertiajs/react"
import {
  CheckVerified01,
  Mail01,
  RefreshCcw01,
  Shield01,
  SlashCircle01,
  Trash01,
  User01,
} from "@untitledui/icons"
import { useState } from "react"

import { DestructiveStackedLeftAlignedModal } from "@/components/ui/destructive-stacked-left-aligned-modal"
import { Dropdown } from "@/components/ui/dropdown-menu"
import { TextArea } from "@/components/ui/textarea"
import {
  adminUserPath,
  profilePath,
  restoreAdminUserPath,
  sendPasswordResetAdminUserPath,
  suspendAdminUserPath,
  toggleAdminAdminUserPath,
  toggleVerifiedAdminUserPath,
  unsuspendAdminUserPath,
} from "@/routes"
import type { SharedData, UserDetail } from "@/types"

type ConfirmableAction = "suspend" | "delete" | null

// The support-actions dots menu. Suspend and delete are two-step (confirm
// modal with an optional evidence note that lands on the account's
// moderation log); everything else fires immediately — feedback arrives via
// the Rails flash → toast wiring.
export function UserActionsMenu({ user }: { user: UserDetail }) {
  const { visitor } = usePage<SharedData>().props
  const isSelf = visitor.email === user.email

  const [confirming, setConfirming] = useState<ConfirmableAction>(null)
  const [note, setNote] = useState("")

  const openConfirm = (action: ConfirmableAction) => {
    setNote("")
    setConfirming(action)
  }

  // Server-side guards remain authoritative; the menu just hides the
  // self-moderation and moderate-an-admin items that would only refuse.
  const canModerate = !isSelf && !user.admin

  return (
    <>
      <Dropdown.Root>
        <Dropdown.DotsButton aria-label="User actions" />
        <Dropdown.Popover>
          <Dropdown.Menu>
            <Dropdown.Section>
              <Dropdown.Item
                onAction={() => {
                  if (user.profile_slug)
                    router.get(profilePath(user.profile_slug))
                  return
                }}
                isDisabled={!user.profile_slug}
                icon={User01}
              >
                View public profile
              </Dropdown.Item>
              {user.verified && user.status === "active" && (
                <Dropdown.Item
                  id="password-reset"
                  label="Send password reset"
                  icon={Mail01}
                  onAction={() =>
                    router.post(sendPasswordResetAdminUserPath(user.id))
                  }
                />
              )}
              <Dropdown.Item
                id="toggle-verified"
                label={
                  user.verified
                    ? "Mark email unverified"
                    : "Mark email verified"
                }
                icon={CheckVerified01}
                onAction={() =>
                  router.patch(toggleVerifiedAdminUserPath(user.id))
                }
              />
              {!isSelf &&
                (user.admin ||
                  user.status === "active" ||
                  user.status === "shell") && (
                  <Dropdown.Item
                    id="toggle-admin"
                    label={
                      user.admin ? "Revoke admin access" : "Grant admin access"
                    }
                    icon={Shield01}
                    onAction={() =>
                      router.patch(toggleAdminAdminUserPath(user.id))
                    }
                  />
                )}
            </Dropdown.Section>
            {canModerate && (
              <Dropdown.Section>
                {user.status === "suspended" ? (
                  <Dropdown.Item
                    id="unsuspend"
                    label="Lift suspension"
                    icon={RefreshCcw01}
                    onAction={() =>
                      router.patch(unsuspendAdminUserPath(user.id))
                    }
                  />
                ) : (
                  user.status !== "deleted" && (
                    <Dropdown.Item
                      id="suspend"
                      label="Suspend account…"
                      icon={SlashCircle01}
                      onAction={() => openConfirm("suspend")}
                    />
                  )
                )}
                {user.status === "deleted" ? (
                  <Dropdown.Item
                    id="restore"
                    label="Restore account"
                    icon={RefreshCcw01}
                    onAction={() => router.patch(restoreAdminUserPath(user.id))}
                  />
                ) : (
                  <Dropdown.Item
                    id="delete"
                    label="Delete account…"
                    icon={Trash01}
                    onAction={() => openConfirm("delete")}
                  />
                )}
              </Dropdown.Section>
            )}
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown.Root>

      <DestructiveStackedLeftAlignedModal
        isOpen={confirming === "suspend"}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={`Suspend ${user.name}?`}
        description="They'll be signed out everywhere and blocked from signing in. Their contributions stay visible. You can lift the suspension at any time."
        confirmLabel="Suspend"
        onConfirm={() =>
          router.patch(suspendAdminUserPath(user.id), {
            note: note || undefined,
          })
        }
      >
        <ReasonField note={note} onChange={setNote} />
      </DestructiveStackedLeftAlignedModal>

      <DestructiveStackedLeftAlignedModal
        isOpen={confirming === "delete"}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={`Delete ${user.name}'s account?`}
        description="They'll be signed out everywhere, their profile hidden, and sign-in blocked. The record is kept for evidence and can be restored."
        confirmLabel="Delete account"
        onConfirm={() =>
          router.delete(adminUserPath(user.id), {
            data: { note: note || undefined },
          })
        }
      >
        <ReasonField note={note} onChange={setNote} />
      </DestructiveStackedLeftAlignedModal>
    </>
  )
}

function ReasonField({
  note,
  onChange,
}: {
  note: string
  onChange: (value: string) => void
}) {
  return (
    <TextArea
      label="Reason"
      hint="Kept on the account's moderation log."
      placeholder="e.g. repeated guideline violations in suggestions"
      value={note}
      onChange={onChange}
      rows={3}
    />
  )
}
